
'use client';

import { useState, useEffect } from 'react';
import Image from 'next/image';
import { GradientText } from '@/components/ui/gradient-text';
import { Loader2 } from 'lucide-react';

interface Client {
  id: number;
  name: string;
  logo_url: string;
  show_name?: boolean;
}

const API_BASE_URL = process.env.NEXT_PUBLIC_BACKEND_API_URL || 'https://api.keerainnovations.com';

async function fetchClients(): Promise<Client[]> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/clients`, { next: { revalidate: 600 } });
    if (!response.ok) {
      console.error("Failed to fetch clients");
      return [];
    }
    const data = await response.json();
    return data.success ? data.data : [];
  } catch (error) {
    console.error("Error fetching clients:", error);
    return [];
  }
}


export function PrestigiousClients() {
  const [clients, setClients] = useState<Client[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function loadClients() {
      setIsLoading(true);
      const data = await fetchClients();
      // For a seamless marquee, we need to duplicate the client list.
      if (data.length > 0) {
        setClients([...data, ...data]);
      } else {
        setClients([]);
      }
      setIsLoading(false);
    }
    loadClients();
  }, []);

  return (
    <section className="py-16 md:py-20 bg-background">
      <div className="container mx-auto px-4 md:px-6">
        <div className="text-center mb-12">
          <h2 className="font-headline text-3xl font-bold tracking-tighter text-white sm:text-5xl">
            Our <GradientText>Prestigious Clients</GradientText>
          </h2>
          <p className="mt-4 max-w-2xl mx-auto text-lg text-muted-foreground">
            We are proud to have worked with some of the best companies in the world.
          </p>
        </div>
        {isLoading ? (
          <div className="flex justify-center items-center h-32">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : clients.length > 0 ? (
          <div className="relative w-full overflow-hidden">
            <div className="flex w-max animate-[marquee_40s_linear_infinite] hover:[animation-play-state:paused]">
              {clients.map((client, index) => (
                <div key={index} className="flex-shrink-0 w-64 h-32 flex flex-col items-center justify-center mx-4 space-y-2">
                  {client.logo_url && <Image
                    src={client.logo_url.startsWith('http') ? client.logo_url : `${API_BASE_URL}${client.logo_url}`}
                    alt={`${client.name} Logo`}
                    width={150}
                    height={80}
                    className="object-contain max-h-20 grayscale opacity-60 hover:grayscale-0 hover:opacity-100 transition-all"
                    data-ai-hint="company logo"
                  />}
                  {client.show_name && (
                    <p className="font-headline text-sm font-medium text-muted-foreground">{client.name}</p>
                  )}
                </div>
              ))}
            </div>
            <div className="absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-background to-transparent"></div>
            <div className="absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-background to-transparent"></div>
          </div>
        ) : (
          <div className="text-center text-muted-foreground">
            <p>Our client list is growing. Check back soon!</p>
          </div>
        )}
      </div>
    </section>
  );
}
